import { useEffect, useState } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";
import { Image as ImageIcon } from "lucide-react";

/* ================= HELPER: NORMALIZE IMAGE LINKS ================= */
const normalizeImageUrl = (url = "") => {
  if (!url) return "";

  // Google Drive
  if (url.includes("drive.google.com")) {
    const match = url.match(/\/d\/([^/]+)/);
    if (match?.[1]) {
      return `https://drive.google.com/uc?id=${match[1]}`;
    }
  }

  // Dropbox
  if (url.includes("dropbox.com")) {
    return url
      .replace("www.dropbox.com", "dl.dropboxusercontent.com")
      .replace("?dl=0", "");
  }

  return url;
};

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dark, setDark] = useState(true);

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const loadTheme = () => {
      const t = localStorage.getItem("theme");
      setDark(t !== "light");
    };

    loadTheme();
    window.addEventListener("theme-change", loadTheme);
    return () => window.removeEventListener("theme-change", loadTheme);
  }, []);

  /* ================= LOAD GALLERY ================= */
  useEffect(() => {
    const loadGallery = async () => {
      try {
        const q = query(
          collection(db, "gallery"),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        setImages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to load gallery:", err);
      } finally {
        setLoading(false);
      }
    };

    loadGallery();
  }, []);

  /* ================= THEME MAP ================= */
  const theme = {
    page: dark
      ? "bg-[#0b0b0b] text-white"
      : "bg-gray-50 text-gray-900",

    card: dark
      ? "bg-white/5 border-white/10"
      : "bg-white border-gray-200",

    desc: dark ? "text-gray-300" : "text-gray-600",

    muted: dark ? "text-gray-400" : "text-gray-500",

    empty: dark ? "text-gray-500" : "text-gray-400",
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${theme.page}`}>
      <div className="max-w-7xl mx-auto px-6 py-20">
        {/* ================= HEADER ================= */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-extrabold mb-3">
            Our Activities
          </h2>
          <p className={`max-w-2xl mx-auto ${theme.muted}`}>
            Moments of dignity, professionalism and care captured
            from our recent services.
          </p>
        </div>

        {/* ================= LOADING ================= */}
        {loading && (
          <div className="flex flex-col items-center py-20">
            <ImageIcon size={40} className="opacity-40 mb-4" />
            <p className={theme.muted}>Loading gallery…</p>
          </div>
        )}

        {/* ================= GALLERY ================= */}
        {!loading && images.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {images.map((img) => {
              const imageSrc = normalizeImageUrl(img.imageUrl);

              return (
                <div
                  key={img.id}
                  className={`group rounded-2xl overflow-hidden border shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${theme.card}`}
                >
                  {/* IMAGE */}
                  <div className="relative h-60 overflow-hidden">
                    <img
                      src={imageSrc}
                      alt={img.description || "Gallery image"}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src =
                          "https://via.placeholder.com/400x300?text=Image+Unavailable";
                      }}
                    />
                  </div>

                  {/* DESCRIPTION */}
                  {img.description && (
                    <div className="p-5">
                      <p className={`text-sm leading-relaxed ${theme.desc}`}>
                        {img.description}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ================= EMPTY ================= */}
        {!loading && images.length === 0 && (
          <div className="text-center py-20">
            <ImageIcon size={42} className="mx-auto opacity-30 mb-4" />
            <p className={theme.empty}>
              No gallery items yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
